<script setup>
import Select from 'primevue/select'
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { menuItems } from '@/core/constants/new-menu-items.constant'
import { useUIStore } from '@/core/stores/ui.store'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { useDssToast } from '@/shared/composables/messages/dss-toast.composable'
import { setCachedCompany } from '@/shared/utils/company-cache.utils'

const { t } = useI18n()
const authStore = useAuthStore()
const uiStore = useUIStore()
const toast = useDssToast()
const router = useRouter()

const selectedClient = ref(null)
const companies = computed(() =>
  [...authStore.companiesList].sort((a, b) => a.name.localeCompare(b.name)),
)
const isChangingCompany = ref(false)
const isSyncingFromAuth = ref(false)
const HOME_ROUTE = menuItems.core[0].route
const SAFETY_ANALYTICS_ROUTE_NAME = 'safety-analytics'
const SAFETY_ANALYTICS_ROLE = 'safety-analytics'

const isLoadingCompanies = computed(() => {
  return companies.value.length === 0 && authStore.isLoading
})

function syncSelectedClient() {
  const companyData = authStore.companyData
  if (companyData && companies.value.length > 0) {
    isSyncingFromAuth.value = true
    selectedClient.value = companies.value.find(c => c.id === companyData.id) || null
    nextTick(() => {
      isSyncingFromAuth.value = false
    })
  }
}

watch([() => authStore.companyData, companies], syncSelectedClient, { immediate: true })

async function handleCompanyChange(event) {
  if (!event.value || isChangingCompany.value || isSyncingFromAuth.value)
    return

  const selectedCompany = event.value

  if (authStore.companyData?.id === selectedCompany.id) {
    return
  }

  try {
    isChangingCompany.value = true
    uiStore.showLoading(t('menu.switching-company'))

    await authStore.validateSelectedCompany(selectedCompany, { isCompanySwitch: true })
    await authStore.fetchUserData(true)

    setCachedCompany(authStore.userData?.b2cId, selectedCompany.id)

    const canKeepSafetyAnalyticsOpen
      = router.currentRoute.value.name === SAFETY_ANALYTICS_ROUTE_NAME
        && authStore.userRoles.includes(SAFETY_ANALYTICS_ROLE)

    if (canKeepSafetyAnalyticsOpen) {
      uiStore.hideLoading()
      return
    }

    await router.push(HOME_ROUTE)

    if (router.currentRoute.value.path === HOME_ROUTE) {
      window.location.reload()
    }

    uiStore.hideLoading()
  }
  catch (error) {
    console.error('[SelectCompany] Error changing company:', error)

    syncSelectedClient()
    uiStore.hideLoading()

    toast.error(
      'messages.error.title',
      'menu.company-switch-failed',
    )
  }
  finally {
    isChangingCompany.value = false
  }
}
</script>

<template>
  <Select
    v-model="selectedClient"
    :options="companies"
    option-label="name"
    filter
    :filter-placeholder="t('menu.search-client')"
    :empty-filter-message="t('common.no-results')"
    :disabled="isChangingCompany || isLoadingCompanies"
    :loading="isLoadingCompanies"
    :pt="{
      overlay: (options) => ({ class: [options?.global?.class, 'mt-2! border! border-dss-surface-200 shadow-[0px_4px_6px_-4px_#0000001A,0px_10px_15px_-3px_#0000001A]'] }),
      label: (options) => ({ class: [options?.global?.class, 'px-2.5 py-2'] }),
      header: (options) => ({ class: [options?.global?.class, 'pt-2.5! pb-4! px-3! bg-white'] }),
      root: (options) => ({ class: [options?.global?.class, 'w-full! ring-0! hover:brightness-90 transition-filter duration-200'] }),
      pcFilter: (options) => ({
        root: { class: [options?.global?.class, 'w-full border-0 placeholder:text-xs outline-none'] },
      }),
      pcFilterContainer: (options) => ({
        root: { class: [options?.global?.class, 'flex flex-row-reverse gap-3 items-center'] },
      }),
      listContainer: (options) => ({
        class: [options?.global?.class, 'mt-2!'],
      }),
      list: (options) => ({
        class: [options?.global?.class, 'py-1!'],
      }),
      option: (options) => ({
        class: [
          options?.global?.class,
          'text-sm! py-2! px-3! font-inter',
          options?.context?.selected && 'bg-dss-surface-100!',
        ],
      }),
    }"
    @change="handleCompanyChange"
  >
    <template #value="slotProps">
      <div
        v-if="isLoadingCompanies"
        class="flex items-center gap-2"
      >
        <span class="text-surface-500">{{ t('auth.login-processing.title') }}</span>
      </div>

      <div
        v-else-if="slotProps.value"
        class="flex items-center"
      >
        <div>{{ slotProps.value.name }}</div>
      </div>

      <span v-else>
        {{ slotProps.placeholder }}
      </span>
    </template>

    <template #option="slotProps">
      <div class="flex items-center">
        <div>{{ slotProps.option.name }}</div>
      </div>
    </template>
  </Select>
</template>
